import { FC, useCallback } from 'react';
import { css } from '@emotion/react';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { generateTrackingId } from '@frontend/call-intel-core';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Chip, Text } from '@frontend/design-system';
import { FeedbackButton } from '../../../feedback-button';

type SearchResultSummaryProps = {
  onSortTypeChange: (sortType: CallIntelTypes.SortType) => void;
  resultCount: number;
  sortType: CallIntelTypes.SortType;
};

const TRACKING_COMPONENT = 'search-summary';

const trackingId = {
  clearSort: generateTrackingId({ component: TRACKING_COMPONENT, context: 'time-sort' }),
  removeTimeSort: generateTrackingId({ component: TRACKING_COMPONENT, context: 'remove-time-sort' }),
  feedbackLink: generateTrackingId({ component: TRACKING_COMPONENT, context: 'feedback-link' }),
  feedbackModal: generateTrackingId({ component: TRACKING_COMPONENT, context: 'feedback-modal' }),
};

const DotSeparator = () => <span css={styles.separator}>&bull;</span>;

export const SearchResultSummary: FC<SearchResultSummaryProps> = ({ onSortTypeChange, resultCount, sortType }) => {
  const { t } = useTranslation('call-intelligence');

  const isSortedByRelevance = sortType === CallIntelTypes.SortType.SORT_RELEVANCE;

  const handleResetToRelevanceSort = useCallback(
    () => onSortTypeChange(CallIntelTypes.SortType.SORT_RELEVANCE),
    [onSortTypeChange]
  );

  return (
    <div css={styles.container}>
      <Text size='medium' weight='bold'>
        {t('{{count}} Results', { count: resultCount })}
      </Text>

      <DotSeparator />

      {isSortedByRelevance ? (
        <Chip css={styles.relevanceSortChip} variant='outline'>
          {t('Sorted by Best Match')}
        </Chip>
      ) : (
        <Chip.Removable
          css={styles.timeSortChip}
          trackingId={trackingId.clearSort}
          removeBtnTrackingId={trackingId.removeTimeSort}
          onClick={handleResetToRelevanceSort}
        >
          {t('Sorted by Time of Call')}
        </Chip.Removable>
      )}

      <DotSeparator />

      <Text color='subdued' size='medium'>
        {t('How are these results?')}
      </Text>

      <FeedbackButton
        label={t('Give Feedback')}
        modalSubtitle={t(
          "Please share as much detail about what's working well, issues you are experiencing, or suggestions for how we can improve the feature."
        )}
        modalTitle={t('How are your Call Intelligence search results?')}
        productName={t('Call Intelligence')}
        showAllowToContact={false}
        showFeedbackIcon={false}
        trackingId={trackingId.feedbackLink}
        trackingIdModalBase={trackingId.feedbackModal}
      />
    </div>
  );
};

const chipBase = css`
  border: none;
  color: ${theme.colors.neutral90};
  max-width: max-content;
`;

const styles = {
  container: css`
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing(1)};
  `,

  relevanceSortChip: css`
    ${chipBase};
    background: ${theme.colors.neutral5};
  `,

  separator: css`
    color: ${theme.colors.neutral90};
  `,

  timeSortChip: css`
    ${chipBase};
    background: ${theme.colors.primary5};
  `,
};
